import { createFeatureSelector, createSelector } from '@ngrx/store';
import type { ProjectState } from '../project.state';
import type { ProductViewState } from './product-view.state';
import { selectProjectEntityTree } from '../project-data/project-data.selectors';
import type { TreeEntity } from '../../../core/models/entity.model';
import { EntityTypeKey } from '../../../shared/constants/entity-type-keys';
import { selectEntityTypes } from '../../../core/store/entity-types/entity-types.selectors';
import type { EntityType } from '../../../core/models/entity-type.model';
import { combineMultiEditProduct } from './helpers/multi-edit-product.helpers';
import { ProductTreeTraverseService } from '../../../core/services/productTreeTraverse.service';

const selectProjectState = createFeatureSelector('project');

const selectProductEntityState = createSelector(selectProjectState, (projectState: ProjectState) => projectState.productViewState);

export const selectUIProductSidebarOpened = createSelector(
  selectProductEntityState,
  (state: ProductViewState) => state.uiProductSidebarOpened
);

export const selectUIProductSidebarRightOpened = createSelector(
  selectProductEntityState,
  (state: ProductViewState) => state.uiProductSidebarRightOpened
);

export const selectMultiEditEnabled = createSelector(selectProductEntityState, state => state.multiEditEnabled);

export const selectMultiEditEntityTypeKey = createSelector(selectProductEntityState, state => state.multiEditSelectedEntityTypeKey);

export const selectMultiEditSelectedEntityIds = createSelector(selectProductEntityState, state => state.multiEditSelectedEntityIds);

export const selectMultiEditSelectedHoodIds = createSelector(selectProductEntityState, state => state.multiEditSelectedHoodIds);

const selectMultiEditEntityType = createSelector(
  selectMultiEditEntityTypeKey,
  selectEntityTypes,
  (typeKey, entityTypes): EntityType | null => {
    if (!typeKey) {
      return null;
    }
    return entityTypes[typeKey];
  }
);

export const selectMultiEditSelectedEntities = createSelector(
  selectProjectEntityTree,
  selectMultiEditSelectedEntityIds,
  (entities, selectedIds) => {
    return selectedIds.map(id => ProductTreeTraverseService.findInTreeById(entities, id)).filter(i => !!i);
  }
);

export const selectMultiEditProductEditorState = createSelector(
  selectMultiEditEntityType,
  selectMultiEditSelectedEntities,
  (selectedType, selectedEntities) => {
    if (!selectedType || selectedEntities.length === 0) {
      return null;
    }

    const combinedEntity = combineMultiEditProduct(selectedType, selectedEntities);

    return {
      type: selectedType,
      combinedEntity: combinedEntity.entity,
      properties: combinedEntity.propertyDetails,
      selectedEntitiesCount: selectedEntities.length
    };
  }
);

export const createMultiEditPropertyEditorForProperty = (propertyKey: string) =>
  createSelector(selectMultiEditProductEditorState, state => state?.properties[propertyKey]);

export interface MultiEditSelectionStatus {
  selected: boolean;
  hasSelectedChildren: boolean;
  allChildrenSelected: boolean;
  selectable: boolean;
}

/**
 * Selects a key-value object where key is an entity id and then value is the multi-edit selection status of the given entity.
 */
export const selectMultiEditSelectionStatuses = createSelector(
  selectMultiEditSelectedEntityIds,
  selectProjectEntityTree,
  selectMultiEditEntityTypeKey,
  (selectedIds, entityTree, entityTypeKey): Record<string, MultiEditSelectionStatus> => {
    const statuses: Record<string, MultiEditSelectionStatus> = {};

    if (!entityTree || !entityTypeKey) {
      return statuses;
    }

    const resolveEntityAndChildSelectionStatus = (entity: TreeEntity): MultiEditSelectionStatus => {
      // If the entity itself is selected, mark it as selected and ignore its children
      if (selectedIds.includes(entity.Id)) {
        statuses[entity.Id] = { allChildrenSelected: false, hasSelectedChildren: false, selected: true, selectable: true };
        return statuses[entity.Id];
      }

      // If the entity has children, then recursively go through all of them to find out if the current entity has any selected
      // children. We need this information to set the "are all"/"are some" children selected -flags appropriately.
      const children = entity.Children.filter(child => child.EntityTypeKey !== EntityTypeKey.Message);
      if (children.length > 0) {
        const childStatuses = children.map(resolveEntityAndChildSelectionStatus);
        const hasSelectableChildren = childStatuses.some(s => s.selectable);
        const allChildrenSelected =
          hasSelectableChildren && childStatuses.filter(c => c.selectable).every(s => s.selected || s.allChildrenSelected);

        statuses[entity.Id] = {
          allChildrenSelected,
          selected: false,
          selectable: entity.EntityTypeKey === entityTypeKey || hasSelectableChildren,
          hasSelectedChildren: !allChildrenSelected && childStatuses.some(s => s.selected || s.allChildrenSelected || s.hasSelectedChildren)
        };
      } else {
        statuses[entity.Id] = {
          allChildrenSelected: false,
          selected: false,
          hasSelectedChildren: false,
          selectable: entity.EntityTypeKey === entityTypeKey
        };
      }

      return statuses[entity.Id];
    };

    resolveEntityAndChildSelectionStatus(entityTree);

    return statuses;
  }
);

export const selectShouldShowMultiEditCheckboxes = createSelector(
  selectMultiEditEnabled,
  selectMultiEditEntityTypeKey,
  (enabled, type): boolean => {
    return enabled && !!type;
  }
);

export const selectOpenHoodLinePanelIds = createSelector(selectProductEntityState, state => state.openHoodLinePanelIds);

export const selectHoodSettingOpened = createSelector(selectProductEntityState, state => state.hoodSettingsOpened);

export const selectOverlappingLineIds = createSelector(selectProductEntityState, state => state.overlappingLineIds);
